import { BACKEND_URL } from "lib/utils/baseUrl";

const STORED_ENDPOINT_KEY = "webPush.subscriptionEndpoint";
const SERVICE_WORKER_URL = "/push-sw.js";

let synchronizationPromise = null;

function createWebPushError(message, code = "WEB_PUSH_ERROR") {
  const error = new Error(message);
  error.code = code;
  return error;
}

async function parseJsonResponse(response) {
  const contentType = response.headers.get("content-type") || "";
  return contentType.includes("application/json")
    ? response.json().catch(() => null)
    : null;
}

async function fetchWithAccessRetry(path, init = {}) {
  const request = () =>
    fetch(`${BACKEND_URL}${path}`, {
      credentials: "include",
      ...init,
      headers: {
        ...(init.body ? { "Content-Type": "application/json" } : {}),
        ...(init.headers || {}),
      },
    });

  let response = await request();
  if (response.status !== 401) return response;

  const refreshResponse = await fetch(`${BACKEND_URL}/user/refresh`, {
    method: "POST",
    credentials: "include",
  });
  if (!refreshResponse.ok) return response;

  response = await request();
  return response;
}

async function requestWebPushApi(path, init = {}) {
  const response = await fetchWithAccessRetry(path, init);
  const data = await parseJsonResponse(response);

  if (!response.ok || data?.success === false) {
    throw createWebPushError(
      data?.message || `브라우저 알림 요청에 실패했습니다. (${response.status})`,
      data?.code || `HTTP_${response.status}`,
    );
  }

  return data || { success: true };
}

function getStoredEndpoint() {
  try {
    return window.localStorage.getItem(STORED_ENDPOINT_KEY) || "";
  } catch {
    return "";
  }
}

function storeEndpoint(endpoint) {
  try {
    if (endpoint) window.localStorage.setItem(STORED_ENDPOINT_KEY, endpoint);
    else window.localStorage.removeItem(STORED_ENDPOINT_KEY);
  } catch {
    // 브라우저 저장소가 차단되어도 PushSubscription 자체는 계속 사용할 수 있다.
  }
}

function urlBase64ToUint8Array(value) {
  const padding = "=".repeat((4 - (value.length % 4)) % 4);
  const base64 = (value + padding).replace(/-/g, "+").replace(/_/g, "/");
  const decoded = window.atob(base64);
  return Uint8Array.from(decoded, (character) => character.charCodeAt(0));
}

function arrayBuffersEqual(left, right) {
  if (!left || !right) return false;

  const leftBytes = new Uint8Array(left);
  const rightBytes = new Uint8Array(right);
  if (leftBytes.length !== rightBytes.length) return false;
  return leftBytes.every((value, index) => value === rightBytes[index]);
}

function subscriptionUsesPublicKey(subscription, publicKeyBytes) {
  return arrayBuffersEqual(
    subscription?.options?.applicationServerKey,
    publicKeyBytes,
  );
}

async function getServerConfiguration() {
  return requestWebPushApi("/user/web-push/config");
}

async function saveSubscription(subscription) {
  const serializedSubscription = subscription.toJSON();
  await requestWebPushApi("/user/web-push/subscriptions", {
    method: "POST",
    body: JSON.stringify({ subscription: serializedSubscription }),
  });
  storeEndpoint(serializedSubscription.endpoint);
}

async function deactivateEndpoint(endpoint) {
  if (!endpoint) return;

  await requestWebPushApi("/user/web-push/subscriptions", {
    method: "DELETE",
    body: JSON.stringify({ endpoint }),
  });
}

export function isWebPushSupported() {
  return Boolean(
    typeof window !== "undefined" &&
      window.isSecureContext &&
      "serviceWorker" in navigator &&
      "PushManager" in window &&
      "Notification" in window,
  );
}

export function getInitialWebPushState() {
  const supported = isWebPushSupported();
  return {
    supported,
    permission: supported ? window.Notification.permission : "unsupported",
    subscribed: false,
    serverRegistered: false,
    serverEnabled: null,
  };
}

export async function getWebPushBrowserState() {
  const state = getInitialWebPushState();
  if (!state.supported) return state;

  const registration = await navigator.serviceWorker.getRegistration();
  const subscription = await registration?.pushManager.getSubscription();
  return {
    ...state,
    subscribed: Boolean(subscription),
    serverRegistered: false,
  };
}

export async function registerWebPushServiceWorker() {
  if (!isWebPushSupported()) {
    throw createWebPushError(
      "이 브라우저에서는 웹 푸시 알림을 사용할 수 없습니다.",
      "WEB_PUSH_UNSUPPORTED",
    );
  }

  return navigator.serviceWorker.register(SERVICE_WORKER_URL, { scope: "/" });
}

async function synchronizeGrantedSubscription({ createIfMissing }) {
  const configuration = await getServerConfiguration();
  if (!configuration.enabled || !configuration.publicKey) {
    return {
      ...(await getWebPushBrowserState()),
      serverRegistered: false,
      serverEnabled: false,
    };
  }

  const registration = await registerWebPushServiceWorker();
  const publicKeyBytes = urlBase64ToUint8Array(configuration.publicKey);
  let subscription = await registration.pushManager.getSubscription();

  if (
    subscription &&
    !subscriptionUsesPublicKey(subscription, publicKeyBytes)
  ) {
    const previousEndpoint = subscription.endpoint;
    await deactivateEndpoint(previousEndpoint).catch(() => undefined);
    await subscription.unsubscribe();
    storeEndpoint("");
    subscription = null;
  }

  if (!subscription && createIfMissing) {
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: publicKeyBytes,
    });
  }

  if (subscription) await saveSubscription(subscription);

  return {
    supported: true,
    permission: window.Notification.permission,
    subscribed: Boolean(subscription),
    serverRegistered: Boolean(subscription),
    serverEnabled: true,
  };
}

export async function synchronizeWebPushSubscription() {
  if (synchronizationPromise) return synchronizationPromise;

  synchronizationPromise = (async () => {
    const state = getInitialWebPushState();
    if (!state.supported) return state;

    if (state.permission === "denied") {
      const storedEndpoint = getStoredEndpoint();
      if (storedEndpoint) {
        await deactivateEndpoint(storedEndpoint).catch(() => undefined);
        storeEndpoint("");
      }
      return state;
    }

    if (state.permission !== "granted") return state;

    try {
      return await synchronizeGrantedSubscription({ createIfMissing: true });
    } catch {
      return getWebPushBrowserState();
    }
  })().finally(() => {
    synchronizationPromise = null;
  });

  return synchronizationPromise;
}

export async function enableWebPushNotifications() {
  if (!isWebPushSupported()) {
    throw createWebPushError(
      "HTTPS 환경과 웹 푸시를 지원하는 브라우저가 필요합니다.",
      "WEB_PUSH_UNSUPPORTED",
    );
  }

  let permission = window.Notification.permission;
  if (permission === "default") {
    permission = await window.Notification.requestPermission();
  }
  if (permission !== "granted") {
    throw createWebPushError(
      permission === "denied"
        ? "브라우저 설정에서 이 사이트의 알림 권한을 허용해 주세요."
        : "브라우저 알림 권한이 허용되지 않았습니다.",
      "WEB_PUSH_PERMISSION_DENIED",
    );
  }

  const configuration = await getServerConfiguration();
  if (!configuration.enabled || !configuration.publicKey) {
    throw createWebPushError(
      "서버의 브라우저 알림 기능이 활성화되어 있지 않습니다.",
      "WEB_PUSH_DISABLED",
    );
  }

  return synchronizeGrantedSubscription({ createIfMissing: true });
}

export async function disableWebPushNotifications() {
  const storedEndpoint = getStoredEndpoint();
  let subscription = null;

  if (isWebPushSupported()) {
    const registration = await navigator.serviceWorker.getRegistration();
    subscription = await registration?.pushManager.getSubscription();
  }

  const endpoint = subscription?.endpoint || storedEndpoint;
  let serverError = null;
  if (endpoint) {
    try {
      await deactivateEndpoint(endpoint);
    } catch (error) {
      serverError = error;
    }
  }

  if (subscription) await subscription.unsubscribe();
  storeEndpoint("");

  if (serverError) throw serverError;
  return getWebPushBrowserState();
}
